import { AfterViewInit, ApplicationRef, ChangeDetectorRef, Component, ElementRef, EnvironmentInjector, HostBinding, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { GridsterConfig } from 'angular-gridster2';
import { ActivatedRoute, Router } from '@angular/router';
import { IdentityService } from 'src/app/services/identity.service';
import { parsePlaceAddress } from 'src/app/services/mux-session.service';
import { WidgetService } from 'src/app/services/widget.service';
import { WidgetConfigService } from 'src/app/services/widget-config.service';
import { ConfigService } from 'src/app/services/config.service';
import { SocketService } from 'src/app/services/socket.service';
import { ChatService } from 'src/app/services/chat.service';
import { SettingsService } from 'src/app/services/settings.service';
import { MuxAgentService } from 'src/app/services/mux-agent.service';
import { BaseWidgetComponent } from '../../strategy-grid/base-widget/base-widget.component';
import { HttpClient } from '@angular/common/http';
import { interval, lastValueFrom, Subscription } from 'rxjs';
import { DynamicWidget } from 'src/app/services/dynamic-widgets.service';
import { GridsterWindowManager, DockPosition, DockviewWindowManager, WindowManager } from 'src/app/services/window-manager';
import { TeamsService } from 'src/app/services/teams.service';

export namespace GridPageComponent {
  export type DashboardContainment = 'overlap' | 'contained' | 'outside';
}

@Component({
    selector: 'app-grid-page',
    templateUrl: './grid-page.component.html',
    styleUrls: ['./grid-page.component.scss'],
    standalone: false
})
export class GridPageComponent implements OnInit, OnDestroy, AfterViewInit {
  @HostBinding('class.menubar-top')
  private get _menubarTop() {
    return this.menubarPosition === 'top';
  }

  @HostBinding('class.menubar-left')
  private get _menubarLeft() {
    return this.menubarPosition === 'left';
  }

  @HostBinding('class.menubar-right')
  private get _menubarRight() {
    return this.menubarPosition === 'right';
  }

  @HostBinding('class.menubar-bottom')
  private get _menubarBottom() {
    return this.menubarPosition === 'bottom';
  }

  @ViewChild('dockviewContainer', { static: false }) dockviewContainer: ElementRef<HTMLElement>;

  private _socketSub: any;
  private _licenseCheckSub: any;
  private _wmEventSub: Subscription;

  /** Dockview panel manifest — tracks open panels for save/restore */
  private _dockviewManifest: { id: string; name: WidgetService.WidgetName; values: any }[] = [];
  private _dockviewRestorePending = false;

  /** The window manager instance */
  public wm: GridsterWindowManager | DockviewWindowManager;

  /** Whether we're in dockview mode */
  public get isDockviewMode(): boolean {
    return this._settings.windowManagerType === 'dockview';
  }

  /** Whether we're in gridster mode */
  public get isGridsterMode(): boolean {
    return this._settings.windowManagerType !== 'dockview';
  }

  /** Type-safe access to gridster window manager (for template bindings) */
  public get gridsterManager(): GridsterWindowManager | null {
    return this.wm instanceof GridsterWindowManager ? this.wm : null;
  }

  public teamName: string;
  private _firstRunChecked = false;
  public isOpen = { board: false, devices: false, data: false, models: false, streams: false };
  public chatEnabled = true;
  public lastShowMeTime: any = new Date();
  public lastShowMe = null;

  /** Convenience accessor — the window manager's items array (for gridster template) */
  public get dashboard() {
    return this.gridsterManager?.items ?? [];
  }

  /** Convenience accessor — gridster options (for template binding) */
  public get options(): GridsterConfig {
    return this.gridsterManager?.options ?? {} as GridsterConfig;
  }

  public get menubarPosition() {
    return this._settings.menubarPosition;
  }

  /**
   * Creates a Widget, adds the widget to the grid, and then saves the grid.
   * @param name The name of the widget to add
   * @param values The raw metadata associated with the widget
   */
  private _addWidget(
    name: WidgetService.WidgetName,
    values?: Partial<WidgetService.Widget>,
    config?: Partial<{
      defaultSpace: WidgetService.GridSpace;
      baseInputs: Record<string, any>;
      extraOutputs: Record<string, any>;
    }>
  ) {
    const onClose = this.closeWidget.bind(this);
    const onRestore = this.restoreWidget.bind(this);
    const onMaximize = this.maximizeWidget.bind(this);
    const onToggle = this.toggleWidget.bind(this);
    const onDock = this.$dockWidget.bind(this);
    const onUndock = this.$undockWidget.bind(this);

    const outputs = { ...(config?.extraOutputs ?? {}), onClose, onRestore, onMaximize, onToggle, onDock, onUndock };

    if (this.isDockviewMode) {
      // Use provided ID (restore) or generate a unique one (new panel)
      let uniqueId = values?.id as string;
      if (!uniqueId || this.wm.hasPanel(uniqueId)) {
        let counter = 1;
        while (this.wm.hasPanel(`${name}${counter}`)) counter++;
        uniqueId = `${name}${counter}`;
      }

      // Dockview mode: create a PanelDescriptor and add via window manager
      const widget = this._widget.createWidget(name, this.teamName, { ...values, id: uniqueId }, {
        defaultSpace: config?.defaultSpace,
        baseInputs: config?.baseInputs,
        outputs
      });
      this.wm.addPanel({
        id: widget.id,
        component: name,
        title: values?.title || config?.baseInputs?.title
          || this._panelTitle(name, values?.inputs?.teamName || config?.baseInputs?.teamName),
        inputs: widget.inputs || {},
        outputs: widget.outputs || {},
        initialSize: { rows: widget.rows, cols: widget.cols },
        position: (values as any)?.position,
        kind: (values as any)?.kind,
      });

      // Track in manifest and persist
      this._dockviewManifest.push({ id: widget.id, name, values: { ...values, id: widget.id, inputs: widget.inputs } });
      this._saveDockviewManifest();
    } else {
      // Gridster mode: use WidgetService's array-based flow
      const widget = this._widget.createWidget(name, this.teamName, values, {
        defaultSpace: config?.defaultSpace,
        baseInputs: config?.baseInputs,
        outputs
      });
      widget.isMaximized = values?.isMaximized;
      this._widget.addWidget(this.teamName, widget);
    }
  }

  public $dockWidget(event: BaseWidgetComponent.DockEvent): void {
    console.log('GridPage.$dockWidget called with event:', event);
    this.dockWidget(event.id, event.position);
  }

  public $undockWidget(event: BaseWidgetComponent.Event): void {
    console.log('GridPage.$undockWidget called with event:', event);
    this.undockWidget(event.id);
  }

  private _removeWidget(...ids: string[]) {
    // use setTimeout, otherwise gridster leaves gridster-preview behind
    setTimeout(() => this._widget.removeWidget(this.teamName, ...ids));
  }

  private static readonly _WIDGET_DISPLAY_NAMES: Record<string, string> = {
    'ChatWidget': 'Work Sessions',
    'HeartbeatWidget': 'Workspace',
    'SettingsWidget': 'Settings',
  };

  private _getWidgetDisplayName(name: string): string {
    return GridPageComponent._WIDGET_DISPLAY_NAMES[name] || name.replace('Widget', '');
  }

  /** The tab title for a new panel. A workspace tab wears its team's name, plain
   *  (Ben 2026-10-26: the workspaces ARE the app — two tabs both called "Workspace"
   *  told him nothing); a remote place reads "team · device". Other panels keep
   *  their widget names. Restored layouts come through here too, so old tabs retitle. */
  private _panelTitle(name: string, teamSlug?: string | null): string {
    if (name !== 'HeartbeatWidget') return this._getWidgetDisplayName(name);
    return this._workspaceTitle(teamSlug || this.teamName);
  }

  private _teamsByKey = new Map<string, string>();
  private _teamsSub: { unsubscribe(): void } | null = null;

  private _workspaceTitle(slug: string | null | undefined): string {
    if (!slug) return this._getWidgetDisplayName('HeartbeatWidget');
    const addr = parsePlaceAddress(slug);
    const label = (s: string) => this._teamsByKey.get(s)
      || (this.identity.me?.teams || []).find((t: any) => (t.slug || t.name) === s)?.name
      || s;
    return addr ? `${label(addr.team)} · ${addr.device}` : label(slug);
  }

  constructor(
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _router: Router,
    private readonly _widget: WidgetService,
    private readonly _widgetConfigService: WidgetConfigService,
    public readonly identity: IdentityService,
    private _socket: SocketService,
    private readonly _settings: SettingsService,
    private readonly _http: HttpClient,
    private readonly _agentService: MuxAgentService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _teams: TeamsService,
    private readonly _injector: EnvironmentInjector,
    private readonly _appRef: ApplicationRef
  ) {
    (<any>window).grid = this;
    this.chatEnabled = ConfigService.features?.chat != false;

    if (this._settings.windowManagerType === 'dockview') {
      this.wm = new DockviewWindowManager(this._injector, this._appRef);
      this.wm.workspaceTitleFor = (slug) => this._workspaceTitle(slug);
    } else {
      this.wm = new GridsterWindowManager(this._settings);
    }
  }

  // === Docking Methods (gridster-only, delegated to window manager) ===

  public isWidgetDocked(widgetId: string): boolean {
    return this.gridsterManager?.isWidgetDocked(widgetId) ?? false;
  }

  public getWidgetDockPosition(widgetId: string): DockPosition | null {
    return this.gridsterManager?.getWidgetDockPosition(widgetId) ?? null;
  }

  public getDockedWidgetIndex(widgetId: string): number {
    return this.gridsterManager?.getDockedWidgetIndex(widgetId) ?? 0;
  }

  public getDockedWidgetCount(position: DockPosition | null): number {
    return this.gridsterManager?.getDockedWidgetCount(position) ?? 0;
  }

  public getDockZoneSize(position: DockPosition | null): number {
    if (!position) return 0;
    return this.gridsterManager?.getDockZoneSize(position) ?? 0;
  }

  public dockWidget(widgetId: string, position: DockPosition): void {
    this.gridsterManager?.dock(widgetId, position);
    this._cdr.detectChanges();
  }

  public undockWidget(widgetId: string): void {
    this.gridsterManager?.undock(widgetId);
    this._cdr.detectChanges();
  }

  public ngOnInit(): void {
    this._widget.registerGrid(this);

    // Team display names for workspace tab titles; restored tabs get the slug until
    // the list arrives, then are retitled.
    this._teamsSub = this._teams.collection$.subscribe((teams) => {
      this._teamsByKey = new Map(teams.map(t => [t.slug || t.name, t.name]));
      (this.wm as DockviewWindowManager)?.retitleWorkspaces?.();
    });

    // Share the backing array between WidgetService and the window manager (gridster only).
    // WidgetService.addWidget() pushes to this array; gridster iterates it.
    if (this.gridsterManager) {
      this._widget.widgets = this.gridsterManager.items;
    }

    // Subscribe to window manager panel events
    this._wmEventSub = this.wm.panelEvent$.subscribe((event) => {
      if (event.type === 'resize' && event.data?.item && event.data?.itemComponent) {
        this._widget.resize$.next({ item: event.data.item, itemComponent: event.data.itemComponent });
        setTimeout(() => this._widget.updateWidget(this.teamName, event.data.item.id, event.data.item), 100);
      }
      // When dockview removes a panel (e.g. user clicks tab X), update manifest
      if (event.type === 'close' && this.isDockviewMode) {
        this._dockviewManifest = this._dockviewManifest.filter(p => p.id !== event.panelId);
        this._saveDockviewManifest();
      }
    });

    // Check license status and add license widget if needed
    this.checkLicenseStatus();
    this.setupLicenseCheckInterval();

    this._activatedRoute.params.subscribe((params) => {
      const prevTeam = this.teamName;
      this.teamName = params.team;
      // The first-run check lists the team's agents and seats, so it waits for
      // the team: run before this point it saw no team, listed the unscoped
      // legacy agents (none) and opened the Settings wizard for every member
      // whose seat and agent the policy had provisioned (cgi.jov.ai, 2026-10-17).
      if (!this._firstRunChecked) {
        this._firstRunChecked = true;
        this.checkFirstTimeSetup();
      }
      if ((<any>window).gridLoaded && params.team === prevTeam) return;
      this.loadDashboard();
    });

    // Handle widget deep-linking via query params (e.g., /#/team/grid?widget=BuildsWidget).
    // A deep link opens ONLY the named widget (no default Chat beside it — see
    // _loadDockviewLayout), focuses it when the saved layout already holds one, and
    // then leaves the URL: the SSO round trip and every reload carried the parameter,
    // so each visit added another copy of the panel (cgi.jov.ai, 2026-10-19).
    this._activatedRoute.queryParams.subscribe((queryParams) => {
      if (queryParams['widget']) {
        // Use setTimeout to ensure dashboard is loaded first
        setTimeout(() => {
          if (queryParams['widget'] === 'MarkdownWidget' && queryParams['path']) {
            const path = queryParams['path'];
            const title = queryParams['title'] || path.split('/').pop() || 'Markdown';
            const src = `${ConfigService.muxUrl}/commander/file?path=${encodeURIComponent(path)}`;
            if (!this._focusOpenDocument('MarkdownWidget', 'src', src)) this.addMarkdownWidget({ src }, title);
          } else {
            const name = queryParams['widget'] as WidgetService.WidgetName;
            if (!this._focusOpenWidget(name)) this.$openWidget(name);
          }
          this._router.navigate([], { relativeTo: this._activatedRoute, replaceUrl: true,
            queryParams: { widget: null, path: null, title: null }, queryParamsHandling: 'merge' });
        }, 100);
      }
    });
    this._widget.restore$.subscribe(() => {
      this.options?.api?.optionsChanged();
    });
    this._widget.maximize$.subscribe(() => {
      this.options?.api?.optionsChanged();
    });
    // Chat-open requests (palette rows, starred rail pins) arrive via service:
    // two menubar instances exist and only this host can open widgets.
    this._widget.openTeamAppFor$.subscribe((ev) => {
      this.$openWidgetForTeam(ev as any);
    });
    this._widget.openChatForTeam$.subscribe((teamSlug) => {
      this.$openChatForTeam(teamSlug);
    });
    this._widget.openAgentSettings$.subscribe(({ agentId, team }) => {
      this._addWidget('AgentSettingsWidget', {}, { baseInputs: team ? { agentId, team } : { agentId } });
    });
    this._socketSub = this._socket.subscribeToRoomMessages('showme').subscribe((response) => {
      const now: any = new Date();
      if (this.identity.me.username !== response.username) {
        return;
      }
      this.lastShowMe = response.component.toLowerCase();
      this.lastShowMeTime = now;

      const component = response.component.toLowerCase();
      if (['experiments', 'training'].includes(component)) {
        this.addExperimentsWidget();
      } else if (['raw', 'upload', 'uploads', 'data'].includes(component)) {
        this.addRawDataWidget();
      } else if (['explore', 'features'].includes(component)) {
        this.addFeatureSelectorWidget();
      } else if (component == 'schemas') {
        this.addSchemasWidget();
      } else if (component == 'builds') {
        this.addBuildsWidget();
      } else if (['jupyterlab', 'jupyter', 'lab'].includes(component)) {
        this.addLabWidget();
      } else if (['audit', 'assets'].includes(component)) {
        this.addAssetsWidget();
      } else if (['design', 'canvas'].includes(component)) {
        const filter = 'filter' in response ? response.filter : null;
        this.addDefinitionsWidget(filter, null);
      } else if (component == 'vault') {
        const vaultName = 'vaultName' in response ? response.vaultName : null;
        if (vaultName) {
          this.addVaultDetailWidget(vaultName);
        } else {
          this.addVaultWidget();
        }
      } else if (['playground'].includes(component)) {
        this.addPlaygroundWidget();
      } else if (['deployment', 'deployments', 'deploys', 'services'].includes(component)) {
        this.addDeploysWidget();
      } else if (['hosts', 'machines'].includes(component)) {
        this.addHostsWidget();
      } else if (['compute', 'devices', 'gpus'].includes(component)) {
        this.addGPUsWidget();
      } else if (component == 'settings') {
        this.addSettingsWidget();
      } else if (component === 'stream') {
        const streamName = 'streamName' in response ? response.streamName : null;
        const modelName = 'modelName' in response ? response.modelName : null;
        if (!streamName) return;

        this.addStreamWidget(streamName, null, null, modelName);
      }
    });
  }

  public ngAfterViewInit(): void {
    // Initialize dockview after the container element is available
    if (this.isDockviewMode && this.dockviewContainer) {
      const dwm = this.wm as DockviewWindowManager;
      dwm.setLayoutStorageKey(this._dockviewLayoutStorageKey);
      dwm.init(this.dockviewContainer.nativeElement);

      // Restore saved panels (deferred from loadDashboard which runs before container exists)
      if (this._dockviewRestorePending) {
        this._dockviewRestorePending = false;
        this._loadDockviewLayout();
      }
    }
  }

  public ngOnDestroy(): void {
    if (this._teamsSub) this._teamsSub.unsubscribe();
    if (this._socketSub) this._socketSub.unsubscribe();
    if (this._licenseCheckSub) this._licenseCheckSub.unsubscribe();
    if (this._wmEventSub) this._wmEventSub.unsubscribe();
    this.wm.dispose();
  }

  public findContainment(a: WidgetService.Widget, b: WidgetService.Widget): GridPageComponent.DashboardContainment {
    function toRect(item) {
      return { left: item.x, right: item.x + item.cols, top: item.y, bottom: item.y + item.rows };
    }
    const aRect = toRect(a);
    const bRect = toRect(b);
    if (
      (aRect.left < bRect.left && aRect.right > bRect.left) ||
      (aRect.left < bRect.right && aRect.right > bRect.right) ||
      (aRect.top < bRect.top && aRect.bottom > bRect.top) ||
      (aRect.top < bRect.bottom && aRect.bottom > bRect.bottom)
    ) {
      return 'overlap';
    }
    if (aRect.left >= bRect.left && aRect.top >= bRect.top && aRect.bottom <= bRect.bottom && aRect.right <= bRect.right) {
      return 'contained';
    }
    return 'outside';
  }

  public loadDashboard(): void {
    (<any>window).gridLoaded = true;

    // Dockview mode: defer to ngAfterViewInit on first load, reload directly after that
    if (this.isDockviewMode) {
      if (this.dockviewContainer) {
        const dwm = this.wm as DockviewWindowManager;
        dwm.clear();
        this._dockviewManifest = [];
        dwm.setLayoutStorageKey(this._dockviewLayoutStorageKey);
        this._loadDockviewLayout();
      } else {
        this._dockviewRestorePending = true;
      }
      return;
    }

    try {
      const storageItem = localStorage.getItem(`${this.teamName}-widgets`);
      if (!storageItem || storageItem.length === 0) return;

      const dashboard = JSON.parse(storageItem) as WidgetService.Widget[];
      if (!dashboard) return;

      for (let item of dashboard) {
        switch (item.name) {
          case 'RestStreamWidget':
            this.addStreamRestWidget(item.inputs.streamName, item.inputs.activeIndicators, item);
            break;
          case 'StreamWidget':
            this.addStreamWidget(item.inputs.streamName, item.inputs.activeIndicators, item);
            break;
          case 'StreamsWidget':
            this.addStreamsWidget(item);
            break;
          case 'ModelChartWidget':
            this.addModelWidget(
              item.inputs.streamName,
              item.inputs.modelName,
              item.inputs.parentId,
              item.inputs.ticker,
              item.inputs.interval,
              item.inputs.parentChartData,
              item
            );
            break;
          case 'TickWidget':
            this.addTickWidget(item.inputs.streamName, item.inputs.symbol, item.inputs.parentId, item.inputs.date);
            break;
          case 'PacketWidget':
            this.addPacketWidget(item.inputs.parentId, item.inputs.packetContent, item);
            break;
          case 'MonacoWidget':
            this.addMonacoWidget(item.inputs.parentId, item.inputs.path, item);
            break;
          case 'ModelInputsWidget':
            this.addModelInputWidget(item.inputs.parentId, item.inputs.timestamp, item.inputs.modelName, item.inputs.modelValues, item);
            break;
          case 'StrategyWidget':
            this.addStrategyWidget(item.inputs.parentId, item.inputs.modelName, item.inputs.ticker, item.inputs.strats, item);
            break;
          case 'StrategyEditorWidget':
            this.addStrategyEditorWidget(item.inputs.parentId, item.inputs.modelName, item.inputs.name, item);
            break;
          case 'BreakoutWidget':
            this.addBreakoutWidget(
              item.inputs.parentId,
              item.inputs.modelName,
              item.inputs.ticker,
              item.inputs.strategyName,
              item.input.days,
              item
            );
            break;
          case 'ExperimentsWidget':
            this.addExperimentsWidget(item.inputs.definitionName, item);
            break;
          case 'PerfTableWidget':
            this.addPerfTableWidget(item.run, item.displayedColumns, item.dynamicColumns, item.live, item);
            break;
          case 'PromptsWidget':
            this.addPromptsWidget(item);
            break;
          case 'JobsWidget':
            this.addJobsWidget(item);
            break;
          case 'LLMWorkflowsWidget':
            this.addLLMWorkflowsWidget(item);
            break;
          case 'TemplateWidget':
            this.addTemplateWidget(item);
            break;
          case 'WorkflowWidget':
            this.addWorkflowWidget(item.inputs.name, item);
            break;
          case 'IndicatorsWidget':
            this.addIndicatorsWidget(item);
            break;
          case 'RawDataWidget':
            this.addRawDataWidget(item);
            break;
          case 'FeatureSelectorWidget':
            this.addFeatureSelectorWidget(item);
            break;
          case 'FeatureEditorWidget':
            this.addFeatureEditorWidget(item);
            break;
          case 'SchemasWidget':
            this.addSchemasWidget(item);
            break;
          case 'BuildsWidget':
            this.addBuildsWidget(item);
            break;
          case 'LabWidget':
            this.addLabWidget(item);
            break;
          case 'AssetsWidget':
            this.addAssetsWidget(item);
            break;
          case 'InspectWidget':
            this.addInspectWidget(item.inputs.parentId, item.inputs.type, item.inputs.assetName, item.inputs.fileName, item);
            break;
          case 'DefinitionsWidget':
            this.addDefinitionsWidget(item.inputs.filter, item);
            break;
          case 'VaultWidget':
            this.addVaultWidget(item);
            break;
          case 'VaultDetailWidget':
            this.addVaultDetailWidget(item.inputs.vaultName, item);
            break;
          case 'PlaygroundWidget':
            this.addPlaygroundWidget(item.inputs?.vaultName, item);
            break;
          case 'DeploysWidget':
            this.addDeploysWidget(item);
            break;
          case 'DeployDetailWidget':
            this.addDeployDetailWidget(item.inputs.serviceName, item);
            break;
          case 'StreamDetailWidget':
            this.addStreamDetailWidget(item.inputs.streamName, item);
            break;
          case 'HostsWidget':
            this.addHostsWidget(item);
            break;
          case 'FilesWidget':
            this.addFilesWidget(item);
            break;
          case 'TerminalWidget':
            this.addTerminalWidget(item.command, item.title, item);
            break;
          case 'GPUsWidget':
            this.addGPUsWidget(item);
            break;
          case 'ChatWidget':
            this.addChatWidget(item);
            break;
          case 'SparklinesWidget':
            this.addSparklinesWidget(item);
            break;
          case 'AgentWidget':
            this.addAgentWidget(item.inputs.model, item.inputs.ticker, item.inputs.interval, item.inputs.presetDateRange, item);
            break;
          case 'SettingsWidget':
            this.addSettingsWidget(item);
            break;
          case 'CompilerWidget':
            this.addCompilerWidget(item.inputs.parentId, item.inputs.rdsName, item);
            break;
          case 'TradingViewWidget':
            this.addTradingViewWidget(item);
            break;
          case 'DataViewerWidget':
            this.addDataViewerWidget(item.inputs.path, item.inputs.fileType, item);
            break;
          case 'MarkdownWidget':
            this.addMarkdownWidget({ src: item.inputs.src, content: item.inputs.content }, item.inputs.title, item);
            break;
          case 'ExternalAppWidget':
            // Reset loaded flag - scripts aren't actually loaded after page refresh
            if (item.inputs.app) {
              item.inputs.app.loaded = false;
            }
            this.addExternalAppWidget(item.inputs.app, item.inputs.config, item.inputs.basePath, item);
            break;
          case 'BuildDetailWidget':
            if (item.inputs.isNew) {
              this.addNewBuildWidget(item.inputs.cloneSchema, item);
            } else {
              this.addBuildDetailWidget(item.inputs.rdsName, item.inputs.runId, item.inputs.username, item.inputs.isArchived, item);
            }
            break;
          case 'KnowledgeGraphWidget':
            this.addKnowledgeGraphWidget(item);
            break;
          case 'BrowserWidget':
            this.addBrowserWidget(item);
            break;
        }
      }

      // Restore docked widget states after all widgets are loaded
      this._restoreDockedWidgets();
    } catch {}
  }

  /**
   * Restore docked widgets from saved state after dashboard load
   */
  private _restoreDockedWidgets(): void {
    this.gridsterManager?.restoreDockedState();
  }

  // --- Dockview persistence ---

  private get _dockviewStorageKey(): string {
    return `${this.teamName}-dockview-manifest`;
  }

  private get _dockviewLayoutStorageKey(): string {
    return `${this.teamName}-dockview-layout`;
  }

  private _saveDockviewManifest(): void {
    try {
      // Strip one-shot values that should not survive a page reload
      const cleaned = this._dockviewManifest.map(entry => {
        if (entry.values?.inputs?.workSessionWarmUp) {
          const { workSessionWarmUp, ...rest } = entry.values.inputs;
          return { ...entry, values: { ...entry.values, inputs: rest } };
        }
        return entry;
      });
      localStorage.setItem(this._dockviewStorageKey, JSON.stringify(cleaned));
    } catch (e) {
      console.warn('[GridPage] Failed to save dockview manifest:', e);
    }
  }

  /**
   * Restore dockview panels: try full layout restore (preserves spatial arrangement),
   * fall back to manifest-only restore (panels as tabs) for older saved state.
   */
  private _loadDockviewLayout(): void {
    const layoutJson = localStorage.getItem(this._dockviewLayoutStorageKey);
    const manifestJson = localStorage.getItem(this._dockviewStorageKey);

    if (layoutJson) {
      // Full layout restore: fromJSON recreates panels with correct spatial arrangement
      try {
        const layoutData = JSON.parse(layoutJson);
        const dwm = this.wm as DockviewWindowManager;
        dwm.restoreLayout({ type: 'dockview', version: 1, data: layoutData });

        // Restore manifest for tracking (used by save/close)
        if (manifestJson) {
          this._dockviewManifest = JSON.parse(manifestJson) || [];
        }

        // Re-wire outputs for ALL panels dockview restored (functions can't survive JSON serialization).
        // Uses the panel's component name to resolve the correct handlers, so this works even when
        // the manifest is out of sync with the actual layout.
        dwm.wireAllOutputs((panelId, component) => {
          const widgetName = component as WidgetService.WidgetName;
          if (!widgetName) return null;
          return this._getOutputsForWidget(widgetName);
        });
        return;
      } catch (e) {
        console.warn('[GridPage] Failed to restore dockview layout, falling back to manifest:', e);
      }
    }

    // Fallback: manifest-only restore (old format, or layout restore failed)
    this._loadDockviewManifestLegacy();

    // If still no panels after all restore attempts, create a default heartbeat —
    // unless the URL deep-links a widget, which then stands alone.
    if (this._dockviewManifest.length === 0 && !this._deepLinkedWidget()) {
      this.addHeartbeatWidget();
    }
  }

  /** The widget named by `?widget=` on the current URL, if any. */
  private _deepLinkedWidget(): string | null {
    return this._activatedRoute.snapshot.queryParams['widget'] || null;
  }

  /** A widget of this kind that is already open is focused, not duplicated. */
  private _focusOpenWidget(name: WidgetService.WidgetName): boolean {
    if (!this.isDockviewMode) return false;
    const hit = this._dockviewManifest.find(e => e.name === name && this.wm.hasPanel(e.id));
    if (!hit) return false;
    this.wm.focusPanel(hit.id);
    return true;
  }

  /** Legacy manifest-only restore (no spatial arrangement) */
  private _loadDockviewManifestLegacy(): void {
    try {
      const stored = localStorage.getItem(this._dockviewStorageKey);
      if (!stored) return;

      const manifest = JSON.parse(stored) as { id: string; name: WidgetService.WidgetName; values: any }[];
      if (!manifest || !Array.isArray(manifest) || manifest.length === 0) return;

      for (const entry of manifest) {
        this._restoreDockviewPanel(entry.name, { ...entry.values, id: entry.id });
      }
    } catch (e) {
      console.warn('[GridPage] Failed to restore dockview manifest:', e);
    }
  }

  /** Get the output handlers for a given widget type (used during layout restore) */
  private _getOutputsForWidget(name: WidgetService.WidgetName): Record<string, any> {
    const base: Record<string, any> = {
      onClose: this.closeWidget.bind(this),
      onRestore: this.restoreWidget.bind(this),
      onMaximize: this.maximizeWidget.bind(this),
      onToggle: this.toggleWidget.bind(this),
      onDock: this.$dockWidget.bind(this),
      onUndock: this.$undockWidget.bind(this),
    };

    // Widget-specific outputs
    switch (name) {
      case 'ChatWidget':
        return {
          ...base,
          onViewExperiments: this.$experiments.bind(this),
          onViewDefinition: this.$definition.bind(this),
          onViewBuild: this.$viewBuild.bind(this),
          onViewAsset: this.$viewAsset.bind(this),
          onViewFeatureExplorer: this.$featureExplorer.bind(this),
          onViewVault: this.$viewVault.bind(this),
          onViewUpload: this.$upload.bind(this),
          onEditMCPConfig: this.$editFile.bind(this),
          onOpenAgentSettings: this.$openAgentSettings.bind(this),
          onOpenBackendDetail: this.$openBackendDetail.bind(this),
        };
      case 'HeartbeatWidget':
        return {
          ...base,
          onOpenAgentSettings: this.$openAgentSettings.bind(this),
          onOpenWorkSession: this.$openWorkSession.bind(this),
        };
      case 'FilesWidget':
        return {
          ...base,
          onEditFile: this.$editFile.bind(this),
          onPreviewFile: this.$previewFile.bind(this),
          onViewMarkdown: this.$viewMarkdown.bind(this),
          onViewCsv: this.$viewCsv.bind(this),
        };
      case 'AgentWidget':
        return {
          ...base,
          onEditFile: this.$editFile.bind(this),
          onStrategyEditor: this.$strategyEditor.bind(this),
        };
      case 'ExperimentsWidget':
        return {
          ...base,
          onProcessView: this.$terminal.bind(this),
          onVaultItem: this.$viewVaultDetail.bind(this),
          onPerfTableView: this.$perfTableView.bind(this),
          onViewDefinition: this.$viewDefinition.bind(this),
          onDiffDefinitions: this.$diffDefinitions.bind(this),
        };
      case 'DefinitionsWidget':
        return {
          ...base,
          onViewAsset: this.$asset.bind(this),
          onViewExperiments: this.$experiments.bind(this),
          onEditFile: this.$editFile.bind(this),
          onCompile: this.$compile.bind(this),
        };
      case 'BuildsWidget':
        return {
          ...base,
          onDiff: this.$openBuildDiff.bind(this),
          onBuildSelect: this.$openBuildDetail.bind(this),
          onNewBuild: this.$openNewBuild.bind(this),
        };
      case 'AssetsWidget':
        return {
          ...base,
          onInspect: this.$inspect.bind(this),
          onDefinition: this.$definition.bind(this),
        };
      case 'VaultWidget':
        return {
          ...base,
          onPlayground: this.$openPlayground.bind(this),
          onEditFile: this.$editFile.bind(this),
        };
      case 'FeatureSelectorWidget':
        return {
          ...base,
          onEditFile: this.$editFile.bind(this),
          onPacketSelect: this.$packetSelect.bind(this),
        };
      case 'FeatureEditorWidget':
        return {
          ...base,
          onEditFile: this.$editFile.bind(this),
        };
      case 'MarkdownWidget':
        return {
          ...base,
          onEditFile: this.$editFile.bind(this),
        };
      case 'HostsWidget':
        return {
          ...base,
          onTerminal: this.$terminal.bind(this),
        };
      case 'PlaygroundWidget':
        return {
          ...base,
          onOpenDetail: this.$openPlaygroundDetail.bind(this),
        };
      case 'DeploysWidget':
        return {
          ...base,
          onOpenDetail: this.$openDeployDetail.bind(this),
        };
      case 'StreamsWidget':
        return {
          ...base,
          onOpenDetail: this.$openStreamDetail.bind(this),
        };
      case 'PromptsWidget':
      case 'LLMWorkflowsWidget':
        return {
          ...base,
          onEditFile: this.$editFile.bind(this),
          onEditWorkflow: this.$editWorkflow.bind(this),
        };
      case 'JobsWidget':
        return {
          ...base,
          onEditFile: this.$editFile.bind(this),
        };
      case 'AgentSettingsWidget':
        return {
          ...base,
          onAgentSaved: this.$agentSaved.bind(this),
          onAgentDeleted: this.$agentDeleted.bind(this),
          onOpenBackendDetail: this.$openBackendDetail.bind(this),
          onEditFile: this.$editFile.bind(this),
        };
      case 'BackendManagerWidget':
      case 'BackendDetailWidget':
        return {
          ...base,
          onOpenTerminal: this.$openTerminal.bind(this),
        };
      default:
        return base;
    }
  }

  /** Re-create a single dockview panel from saved manifest entry */
  private _restoreDockviewPanel(name: WidgetService.WidgetName, item: any): void {
    switch (name) {
      case 'ChatWidget': this.addChatWidget(item); break;
      case 'FilesWidget': this.addFilesWidget(item); break;
      case 'TerminalWidget': this.addTerminalWidget(item?.inputs?.command, item?.inputs?.title, item); break;
      case 'SettingsWidget': this.addSettingsWidget(item); break;
      case 'ExperimentsWidget': this.addExperimentsWidget(item?.inputs?.definitionName, item); break;
      case 'DefinitionsWidget': this.addDefinitionsWidget(item?.inputs?.filter, item); break;
      case 'BuildsWidget': this.addBuildsWidget(item); break;
      case 'AssetsWidget': this.addAssetsWidget(item); break;
      case 'RawDataWidget': this.addRawDataWidget(item); break;
      case 'FeatureSelectorWidget': this.addFeatureSelectorWidget(item); break;
      case 'FeatureEditorWidget': this.addFeatureEditorWidget(item); break;
      case 'VaultWidget': this.addVaultWidget(item); break;
      case 'PlaygroundWidget': this.addPlaygroundWidget(item?.inputs?.vaultName, item); break;
      case 'DeploysWidget': this.addDeploysWidget(item); break;
      case 'JobsWidget': this.addJobsWidget(item); break;
      case 'PromptsWidget': this.addPromptsWidget(item); break;
      case 'StreamsWidget': this.addStreamsWidget(item); break;
      case 'HostsWidget': this.addHostsWidget(item); break;
      case 'GPUsWidget': this.addGPUsWidget(item); break;
      case 'MarkdownWidget': this.addMarkdownWidget({ src: item?.inputs?.src, content: item?.inputs?.content }, item?.inputs?.title, item); break;
      case 'DataViewerWidget': this.addDataViewerWidget(item?.inputs?.path || item?.inputs?.directPath, item?.inputs?.fileType, item); break;
      case 'KnowledgeGraphWidget': this.addKnowledgeGraphWidget(item); break;
      case 'BrowserWidget': this.addBrowserWidget(item); break;
      case 'ExternalAppWidget': this.addExternalAppWidget(item?.inputs?.app, item?.inputs?.config, item?.inputs?.basePath, item); break;
      default:
        console.warn(`[GridPage] Unknown widget in dockview manifest: ${name}`);
        break;
    }
  }

  public addStreamWidget(streamName: string, activeIndicators = [], values = null, modelName = null, ticker = null): void {
    this._addWidget('StreamWidget', values, {
      defaultSpace: { rows: 25, cols: 50 },
      baseInputs: { streamName, activeIndicators, modelName, ticker }, // Add ticker here
      extraOutputs: {
        onModelSelect: this.$modelSelect.bind(this),
        onIndicatorSelect: this.$indicatorSelect.bind(this),
        onCloseChildren: this.$closeChildren.bind(this),
        onTickSelect: this.$tickSelect.bind(this),
        onStrategy: this.$strategy.bind(this)
      }
    });
  }

  public addStreamRestWidget(streamName: string, activeIndicators = [], values = null, modelName = null): void {
    this._addWidget('RestStreamWidget', values, {
      defaultSpace: { rows: 25, cols: 50 },
      baseInputs: { name: streamName },
      extraOutputs: { onIFrameFocus: this.$iFrameFocus.bind(this) }
    });
  }

  public addStreamsWidget(values = null): void {
    this._addWidget('StreamsWidget', values, {
      extraOutputs: {
        onOpenDetail: this.$openStreamDetail.bind(this)
      }
    });
  }

  public $openStreamDetail(input: { parentId: string; serviceName: string }): void {
    this.addStreamDetailWidget(input.serviceName);
  }

  public addStreamDetailWidget(streamName: string, values = null): void {
    this._addWidget('StreamDetailWidget', values, {
      baseInputs: { streamName },
      defaultSpace: { rows: 35, cols: 55 }
    });
  }

  public addModelWidget(
    streamName: string,
    modelName: string,
    parentId: string,
    ticker: string,
    interval: string = "1day",
    parentChartData: any[] = [],
    values = null
  ): void {
    const parent = this.dashboard.find(({ id }) => id === parentId);

    this._addWidget('ModelChartWidget', values, {
      defaultSpace: { x: parent?.x, y: parent?.y + parent?.rows, rows: 15, cols: parent?.cols },
      baseInputs: { parentId, streamName, modelName, ticker, interval, parentChartData },
      extraOutputs: { onPredictSelect: this.$predictSelect.bind(this), onStrategyEditor: this.$strategyEditor.bind(this) }
    });
  }

  public addIndicatorWidget(
    streamName: string,
    parentId: string,
    ticker: string,
    indicator: any,
    parentChartData: any[],
    values = null
  ): void {
    const parent = this.dashboard.find(({ id }) => id === parentId);

    this._addWidget('IndicatorWidget', values, {
      defaultSpace: { x: parent.x, y: parent.y + parent.rows, rows: 15, cols: parent.cols },
      baseInputs: { parentId, streamName, indicator, ticker, parentChartData }
    });
  }

  public addTickWidget(streamName: string, symbol: string, parentId: string, date: Date, values = null): void {
    this._addWidget('TickWidget', values, {
      defaultSpace: { y: 3, rows: 20, cols: 50 },
      baseInputs: { parentId, streamName, symbol, date },
      extraOutputs: { onPacketSelect: this.$packetSelect.bind(this) }
    });
  }

  public addPacketWidget(parentId: string, packetContent: any, values = null): void {
    this._addWidget('PacketWidget', values, {
      defaultSpace: { y: 4, rows: 20, cols: 40 },
      baseInputs: { parentId, packetContent },
    });
  }

  public addMonacoWidget(parentId: string, path: any, values = null, host = ''): void {
    this._addWidget('MonacoWidget', values, {
      defaultSpace: { y: 1, rows: 40, cols: 40 },
      baseInputs: { parentId, path, host },
      extraOutputs: {}
    });
  }

  public addAgentSettingsWidget(agentId: string | null, values = null): void {
    this._addWidget('AgentSettingsWidget', values, {
      defaultSpace: { y: 1, rows: 40, cols: 35 },
      baseInputs: { agentId },
      extraOutputs: {
        onAgentSaved: this.$agentSaved.bind(this),
        onAgentDeleted: this.$agentDeleted.bind(this),
        onOpenBackendDetail: this.$openBackendDetail.bind(this),
        onEditFile: this.$editFile.bind(this)
      }
    });
  }

  public addBackendManagerWidget(values = null): void {
    this._addWidget('BackendManagerWidget', values, {
      defaultSpace: { y: 1, rows: 35, cols: 40 },
      extraOutputs: {
        onOpenTerminal: this.$openTerminal.bind(this)
      }
    });
  }

  public addBackendDetailWidget(backendId: string, credentialsId?: string, values = null): void {
    const baseInputs: any = { backendId };
    if (credentialsId) {
      baseInputs.credentialsId = credentialsId;
    }
    this._addWidget('BackendDetailWidget', values, {
      defaultSpace: { y: 1, rows: 30, cols: 35 },
      baseInputs,
      extraOutputs: {
        onOpenTerminal: this.$openTerminal.bind(this)
      }
    });
  }

  public $openBackendDetail(event: { backendId: string; credentialsId?: string }): void {
    this.addBackendDetailWidget(event.backendId, event.credentialsId);
  }

  public $focusPanel(panelId: string): void {
    if (this.isDockviewMode && this.wm.hasPanel(panelId)) this.wm.focusPanel(panelId);
  }

  /** host.badge.set → count on the panel's tab (dockview only). */
  public $setPanelBadge(panelId: string, count: number | null): void {
    if (this.isDockviewMode) this.wm.setPanelBadge(panelId, count);
  }

  public $openTerminal(): void {
    this.addTerminalWidget(null, null);
  }

  public $agentSaved(event: { agentId: string }): void {
    console.log('Agent saved:', event.agentId);
    // The chat widget will pick up the change via the agent service
  }

  public $agentDeleted(event: { agentId: string }): void {
    console.log('Agent deleted:', event.agentId);
    // The chat widget will pick up the change via the agent service
  }

  public addModelInputWidget(parentId: string, timestamp: string, modelName: string, modelValues: any, values = null): void {
    this._addWidget('ModelInputsWidget', values, {
      baseInputs: { parentId, timestamp, modelName, modelValues },
      extraOutputs: { onPredictSelect: this.$predictSelect.bind(this) }
    });
  }

  public addStrategyWidget(parentId: string, modelName: string, ticker: string, strats: string[], values = null): void {
    this._addWidget('StrategyWidget', values, {
      defaultSpace: { rows: 23, cols: 50 },
      baseInputs: { parentId, modelName, ticker, strats },
      extraOutputs: { onLoadBreakout: this.$loadBreakout.bind(this) }
    });
  }

  public addStrategyEditorWidget(parentId: string, modelName: string, name: string, values = null): void {
    this._addWidget('StrategyEditorWidget', values, {
      defaultSpace: { rows: 23, cols: 50 },
      baseInputs: { parentId, modelName, name },
      extraOutputs: {}
    });
  }

  public addBreakoutWidget(parentId: string, modelName: string, ticker: string, strategyName, days, values = null): void {
    this._addWidget('BreakoutWidget', values, {
      defaultSpace: { x: 4, y: 2, rows: 30, cols: 40 },
      baseInputs: { parentId, modelName, ticker, strategyName, days }
    });
  }

  public addExperimentsWidget(definitionName: string = null, values = null): void {
    this._addWidget('ExperimentsWidget', values, {
      baseInputs: { definitionName },
      extraOutputs: {
        onProcessView: this.$terminal.bind(this),
        onVaultItem: this.$viewVaultDetail.bind(this),
        onPerfTableView: this.$perfTableView.bind(this),
        onViewDefinition: this.$viewDefinition.bind(this),
        onDiffDefinitions: this.$diffDefinitions.bind(this)
      }
    });
  }

  public addPerfTableWidget(run: any, displayedColumns: string[], dynamicColumns: any[], live: boolean, values = null) {
    this._addWidget('PerfTableWidget', values, {
      baseInputs: { run, displayedColumns, dynamicColumns, live }
    });
  }

  public addRawDataWidget(values = null): void {
    this._addWidget('RawDataWidget', values);
  }

  public addPromptsWidget(values = null): void {
    console.log('Adding PromptsWidget', values);
    
    // Define context-specific handlers for grid mode
    const contextHandlers = {
      onEditFile: this.$editFile.bind(this),
      onEditWorkflow: this.$editWorkflow.bind(this)
    };

    // Get widget configuration
    const config = this._widgetConfigService.getWidgetConfig('PromptsWidget', 'grid', contextHandlers);
    
    this._addWidget('PromptsWidget', values, {
      extraOutputs: config.outputs
    });
  }

  public addJobsWidget(values = null): void {
    // Define context-specific handlers for grid mode
    const contextHandlers = {
      onEditFile: this.$editFile.bind(this)
    };

    // Get widget configuration
    const config = this._widgetConfigService.getWidgetConfig('JobsWidget', 'grid', contextHandlers);
    
    this._addWidget('JobsWidget', values, {
      extraOutputs: config.outputs
    });
  }

  public addLLMWorkflowsWidget(values = null): void {
    // Define context-specific handlers for grid mode
    const contextHandlers = {
      onEditFile: this.$editFile.bind(this),
      onEditWorkflow: this.$editWorkflow.bind(this)
    };

    // Get widget configuration
    const config = this._widgetConfigService.getWidgetConfig('LLMWorkflowsWidget', 'grid', contextHandlers);
    
    this._addWidget('LLMWorkflowsWidget', values, {
      extraOutputs: config.outputs
    });
  }

  public addTemplateWidget(values = null): void {
    this._addWidget('TemplateWidget', values, {
      extraOutputs: { }
    });
  }

  public addWorkflowWidget(name: any, values = null): void {
    this._addWidget('WorkflowWidget', values, {
      baseInputs: { name },
      defaultSpace: { x: 3, y: 3 },
      extraOutputs: {
        onEditFile: this.$editFile.bind(this)
      }
    });
  }

  public addIndicatorsWidget(values = null): void {
    this._addWidget('IndicatorsWidget', values, {
      extraOutputs: {
        onEditFile: this.$editFile.bind(this)
      }
    });
  }

  public addFeatureSelectorWidget(values = null): void {
    this._addWidget('FeatureSelectorWidget', values, {
      defaultSpace: { minItemCols: 20, minItemRows: 15, cols: 50, rows: 35 },
      extraOutputs: {
        onEditFile: this.$editFile.bind(this),
        onPacketSelect: this.$packetSelect.bind(this)
      }
    });
  }

  public addFeatureEditorWidget(values = null): void {
    this._addWidget('FeatureEditorWidget', values, {
      defaultSpace: { cols: 50, rows: 35 },
      extraOutputs: {
        onEditFile: this.$editFile.bind(this)
      }
    });
  }

  public addSchemasWidget(values = null): void {
    this._addWidget('SchemasWidget', values, {
      extraOutputs: { onBuild: this.$viewBuild.bind(this) }
    });
  }

  public addBuildsWidget(values = null): void {
    this._addWidget('BuildsWidget', values, {
      extraOutputs: {
        onDiff: this.$openBuildDiff.bind(this),
        onBuildSelect: this.$openBuildDetail.bind(this),
        onNewBuild: this.$openNewBuild.bind(this)
      }
    });
  }

  public $openBuildDiff(input: { parentId: string; left: string; right: string }): void {
    this.addModelDiffWidget(input.left, input.right);
  }

  public $openBuildDetail(input: { parentId: string; rdsName: string; runId: number; username: string; isArchived: boolean }): void {
    this.addBuildDetailWidget(input.rdsName, input.runId, input.username, input.isArchived);
  }

  public $openNewBuild(input: { parentId: string; cloneSchema?: any }): void {
    console.log('$openNewBuild called', input);
    this.addNewBuildWidget(input.cloneSchema);
  }

  public addModelDiffWidget(left: string, right: string, values = null): void {
    this._addWidget('ModelDiffWidget', values, {
      baseInputs: { left, right },
      defaultSpace: { rows: 35, cols: 55 }
    });
  }

  public addBuildDetailWidget(rdsName: string, runId: number, username: string, isArchived: boolean, values = null): void {
    this._addWidget('BuildDetailWidget', values, {
      baseInputs: { rdsName, runId, username, isArchived },
      defaultSpace: { rows: 35, cols: 55 },
      extraOutputs: {
        onAsset: this.$openAsset.bind(this),
        onTerminal: this.$openProcessTerminal.bind(this),
        onLog: this.$openBuildLog.bind(this),
        onBuildStarted: this.$onBuildStarted.bind(this)
      }
    });
  }

  public addNewBuildWidget(cloneSchema: any = null, values = null): void {
    const rdsName = `build_${Date.now().toString()}`;
    this._addWidget('BuildDetailWidget', values, {
      baseInputs: { isNew: true, rdsName, cloneSchema },
      defaultSpace: { rows: 35, cols: 55 },
      extraOutputs: {
        onBuildStarted: this.$onBuildStarted.bind(this)
      }
    });
  }

  public $openProcessTerminal(input: { parentId: string; command: string; title: string }): void {
    this.addTerminalWidget(input.command, input.title);
  }

  public async $openBuildLog(input: { parentId: string; runId: number; rdsName: string; isArchived: boolean }): Promise<void> {
    const config = await this._teams.getConfigAsync(this.teamName);
    const logPath = `${config.path.logs}/${input.runId}/workflow.log`;
    this.addMonacoWidget(input.parentId, logPath);
  }

  public $onBuildStarted(input: { parentId: string; build: any }): void {
    // When a new build is started, open the build detail widget for it
    if (input.build) {
      this.addBuildDetailWidget(
        input.build.rdsName,
        input.build.runid,
        input.build.user,
        false
      );
    }
  }

  public addAssetsWidget(values = null): void {
    const inputs = values && Object.keys(values).includes('assetName') ? { assetName: values['assetName'] } : null;
    this._addWidget('AssetsWidget', values, {
      baseInputs: inputs,
      extraOutputs: {
        onInspect: this.$inspect.bind(this),
        onDefinition: this.$definition.bind(this)
      }
    });
  }

  public addInspectWidget(parentId: string, type: string, assetName: string, fileName: string, values = null) {
    this._addWidget('InspectWidget', values, {
      baseInputs: { parentId, type, assetName, fileName }
    });
  }

  public addDefinitionsWidget(filter: string = null, values = null): void {
    this._addWidget('DefinitionsWidget', values, {
      baseInputs: { filter },
      extraOutputs: {
        onViewAsset: this.$asset.bind(this),
        onViewExperiments: this.$experiments.bind(this),
        onEditFile: this.$editFile.bind(this),
        onCompile: this.$compile.bind(this)
      }
    });
  }

  public addVaultWidget(values = null): void {
    this._addWidget('VaultWidget', values, {
      extraOutputs: {
        onPlayground: this.$openPlayground.bind(this),
        onEditFile: this.$editFile.bind(this)
      }
    });
  }

  public $openVaultDetail(input: { parentId: string; vaultName: string }): void {
    this.addVaultDetailWidget(input.vaultName);
  }

  public $openPlayground(input: { parentId: string; vaultName: string }): void {
    this.addPlaygroundWidget(input.vaultName);
  }

  public addVaultDetailWidget(vaultName: string, values = null): void {
    this._addWidget('VaultDetailWidget', values, {
      baseInputs: { vaultName },
      defaultSpace: { rows: 35, cols: 55 }
    });
  }

  public addPlaygroundWidget(vaultName: string = null, values = null): void {
    this._addWidget('PlaygroundWidget', values, {
      baseInputs: { vaultName },
      defaultSpace: { rows: 35, cols: 55 },
      extraOutputs: {
        onOpenDetail: this.$openPlaygroundDetail.bind(this)
      }
    });
  }

  public $openPlaygroundDetail(input: { vaultName: string; sampleIndex: number; sampleData: any }): void {
    console.log('[GridPage] $openPlaygroundDetail called', input);
    this.addPlaygroundDetailWidget(input.vaultName, input.sampleIndex, input.sampleData);
  }

  public addPlaygroundDetailWidget(vaultName: string, sampleIndex: number, sampleData: any, values = null): void {
    this._addWidget('PlaygroundDetailWidget', values, {
      baseInputs: { vaultName, sampleIndex, sampleData },
      defaultSpace: { rows: 35, cols: 40 }
    });
  }

  public addDeploysWidget(values = null): void {
    this._addWidget('DeploysWidget', values, {
      extraOutputs: {
        onOpenDetail: this.$openDeployDetail.bind(this)
      }
    });
  }

  public $openDeployDetail(input: { parentId: string; serviceName: string }): void {
    this.addDeployDetailWidget(input.serviceName);
  }

  public addDeployDetailWidget(serviceName: string, values = null): void {
    this._addWidget('DeployDetailWidget', values, {
      baseInputs: { serviceName },
      defaultSpace: { rows: 35, cols: 55 }
    });
  }

  public addHostsWidget(values = null): void {
    this._addWidget('HostsWidget', values, {
      extraOutputs: { onTerminal: this.$terminal.bind(this) }
    });
  }

  public addBrowserWidget(values = null): void {
    this._addWidget('BrowserWidget', values, {
      defaultSpace: { rows: 35, cols: 55 }
    });
  }

  public addFilesWidget(values = null): void {
    this._addWidget('FilesWidget', values, {
      extraOutputs: {
        onEditFile: this.$editFile.bind(this),
        onPreviewFile: this.$previewFile.bind(this),
        onViewMarkdown: this.$viewMarkdown.bind(this),
        onViewCsv: this.$viewCsv.bind(this)
      }
    });
  }

  public addPreviewWidget(path: string, previewType: 'image' | 'pdf', values = null, host = ''): void {
    this._addWidget('PreviewWidget', values, {
      baseInputs: { path, previewType, host },
      defaultSpace: { cols: 40, rows: 30 }
    });
  }

  public addDataViewerWidget(path: string, fileType: string, values = null): void {
    this._addWidget('DataViewerWidget', values, {
      baseInputs: { path, fileType },
      defaultSpace: { cols: 55, rows: 35 }
    });
  }

  public addTerminalWidget(command: string, title: string, values = null): void {
    this._addWidget('TerminalWidget', values, {
      baseInputs: { command, title }
    });
  }

  public addGPUsWidget(values = null): void {
    this._addWidget('GPUsWidget', values);
  }

  public addLabWidget(values = null): void {
    this._addWidget('LabWidget', values);
  }

  public addSparklinesWidget(values = null): void {
    this._addWidget('SparklinesWidget', values);
  }

  public addAgentWidget(
    model: string | null,
    ticker: string | null,
    interval: string | null,
    presetDateRange: string | null,
    values = null
  ): void {
    this._addWidget('AgentWidget', values, {
      baseInputs: {
        model,
        ticker,
        interval,
        presetDateRange
      },
      extraOutputs: {
        onEditFile: this.$editFile.bind(this),
        onStrategyEditor: this.$strategyEditor.bind(this)
      }
    });
  }

  public addChatWidget(values = null): void {
    this._addWidget('ChatWidget', values, {
      defaultSpace: { minItemCols: 20, minItemRows: 20, cols: 60, rows: 35 },
      baseInputs: { isFullScreen: false }, // Explicitly set isFullScreen to false
      extraOutputs: {
        onViewExperiments: this.$experiments.bind(this),
        onViewDefinition: this.$definition.bind(this),
        onViewBuild: this.$viewBuild.bind(this),
        onViewAsset: this.$viewAsset.bind(this),
        onViewFeatureExplorer: this.$featureExplorer.bind(this),
        onViewVault: this.$viewVault.bind(this),
        onViewUpload: this.$upload.bind(this),
        onEditMCPConfig: this.$editFile.bind(this),
        onOpenAgentSettings: this.$openAgentSettings.bind(this),
        onOpenBackendDetail: this.$openBackendDetail.bind(this)
      }
    });
  }

  public addHeartbeatWidget(values = null): void {
    this._addWidget('HeartbeatWidget', values, {
      defaultSpace: { minItemCols: 20, minItemRows: 15, cols: 50, rows: 30 },
      extraOutputs: {
        onOpenAgentSettings: this.$openAgentSettings.bind(this),
        onOpenWorkSession: this.$openWorkSession.bind(this),
      },
    });
  }

  /** Chat widget scoped to a specific team (command palette "Open chat: …").
   *  values.inputs wins over the page team in createWidget, so the widget
   *  stands in the given team while the workspace stays put. Singleton by
   *  default: an already-open chat for that team is focused, not duplicated. */
  public $openChatForTeam(teamSlug: string): void {
    const existingId = this._findTeamChatPanel(teamSlug);
    if (existingId) {
      this.wm.focusPanel(existingId);
      return;
    }
    this.addHeartbeatWidget({ inputs: { teamName: teamSlug } });
  }

  private _findTeamChatPanel(teamSlug: string): string | null {
    if (this.isDockviewMode) {
      const entry = this._dockviewManifest.find(
        e => e.name === 'HeartbeatWidget' && e.values?.inputs?.teamName === teamSlug
      );
      return entry?.id ?? null;
    } else {
      const widget = this._widget.widgets.find(
        w => w.name === 'HeartbeatWidget' && w.inputs?.teamName === teamSlug
      );
      return widget?.id ?? null;
    }
  }

  public addKnowledgeGraphWidget(values = null): void {
    this._addWidget('KnowledgeGraphWidget', values, {
      defaultSpace: { minItemCols: 20, minItemRows: 15, cols: 40, rows: 25 },
    });
  }

  public $openWorkSession(event: { sessionId: string; agentId: string; title: string; channelId?: string; warmUp?: string }): void {
    // Singleton: if a widget for this work session already exists, focus it instead of opening a duplicate
    const existingId = this._findWorkSessionPanel(event.sessionId);
    if (existingId) {
      this.wm.focusPanel(existingId);
      return;
    }

    // In dockview mode, split right so the heartbeat channel stays visible
    let position: any;
    if (this.isDockviewMode && event.channelId) {
      const dwm = this.wm as DockviewWindowManager;
      if (dwm.groupIds.length === 1) {
        position = { referenceGroup: dwm.groupIds[0], direction: 'right' };
      }
    }

    this.addChatWidget({
      title: event.title,
      position,
      inputs: {
        workSessionId: event.sessionId,
        workSessionAgentId: event.agentId,
        workSessionChannelId: event.channelId,
        workSessionWarmUp: event.warmUp,
        workSessionStartCollapsed: !!event.channelId,
      }
    });
  }

  /** Find an existing panel that is showing the given work session */
  private _findWorkSessionPanel(sessionId: string): string | null {
    if (this.isDockviewMode) {
      const entry = this._dockviewManifest.find(
        e => e.name === 'ChatWidget' && e.values?.inputs?.workSessionId === sessionId
      );
      return entry?.id ?? null;
    } else {
      const widget = this._widget.widgets.find(
        w => w.name === 'ChatWidget' && w.inputs?.workSessionId === sessionId
      );
      return widget?.id ?? null;
    }
  }

  public $openAgentSettings(event: { agentId: string | null }): void {
    console.log("$openAgentSettings", event)
    this.addAgentSettingsWidget(event.agentId);
  }

  public addSettingsWidget(values = null, initialTab: 'setup' | 'system' | 'theme' | 'display' | 'gpus' | null = null): void {
    this._addWidget('SettingsWidget', values, {
      defaultSpace: { minItemCols: 27, rows: 30, cols: 47 },
      baseInputs: initialTab ? { initialTab } : undefined,
      extraOutputs: {
        onStartChat: () => {
          const chatApp = ConfigService.chatApp;
          if (chatApp?.inputs && Object.keys(chatApp.inputs).length > 0) {
            this.$openExternalApp(chatApp.inputs);
          } else if (chatApp?.widget) {
            this.$openWidget(chatApp.widget as WidgetService.WidgetName);
          } else {
            this.addHeartbeatWidget();
          }
        }
      }
    });
  }

  public addCompilerWidget(parentId: string, rdsName: string, values = null): void {
    this._addWidget('CompilerWidget', values, {
      baseInputs: { parentId, rdsName },
      defaultSpace: { minItemCols: 41, minItemRows: 25 }
    });
  }

  public addLicenseWidget(values = null): void {
    // Only allow one license widget at a time
    const existingWidget = this.dashboard.find(widget => widget.name === 'LicenseWidget');
    if (existingWidget) {
      return;
    }
    this._addWidget('LicenseWidget', values, {
      defaultSpace: { x: 2, y: 2, rows: 22, cols: 35 }
    });
  }

  public addTeamPolicyWidget(values = null): void {
    // One policy page at a time (docs/plans/org-policy.md)
    const existingWidget = this.dashboard.find(widget => widget.name === 'TeamPolicyWidget');
    if (existingWidget) {
      return;
    }
    this._addWidget('TeamPolicyWidget', values, {
      defaultSpace: { x: 2, y: 2, rows: 40, cols: 60 }
    });
  }

  public addTrustsWidget(values = null): void {
    // One Trusts page at a time (docs/plans/trusts-2026-10-06.md)
    if (this.dashboard.find(widget => widget.name === 'TrustsWidget')) return;
    this._addWidget('TrustsWidget', values, {
      defaultSpace: { x: 2, y: 2, rows: 40, cols: 60 }
    });
  }

  public addTradingViewWidget(values = null): void {
    this._addWidget('TradingViewWidget', values, {
      defaultSpace: { rows: 40, cols: 60 }
    });
  }

  public closeWidget($event: BaseWidgetComponent.Event): void {
    if (this.isDockviewMode) {
      this.wm.removePanel($event.id);
      this._dockviewManifest = this._dockviewManifest.filter(p => p.id !== $event.id);
      this._saveDockviewManifest();
    } else {
      // If the widget is docked, undock it first to clean up the dock zone
      if ((this.wm as GridsterWindowManager).isWidgetDocked($event.id)) {
        (this.wm as GridsterWindowManager).undock($event.id);
      }
      this._removeWidget($event.id);
    }
  }

  public restoreWidget($event: BaseWidgetComponent.Event): void {
    this._widget.restoreWidget(this.teamName, $event.id);
  }

  public maximizeWidget($event: BaseWidgetComponent.Event): void {
    this._widget.maximizeWidget(this.teamName, $event.id);
  }

  public toggleWidget($event: BaseWidgetComponent.Event): void {
    this._widget.toggleWidget(this.teamName, $event.id);
  }

  public openSettings(): void {
    this.addSettingsWidget();
  }

  public $closeChildren(children: string[]): void {
    this._removeWidget(...children);
  }

  public $modelSelect(input: any): void {
    this.addModelWidget(input.streamName, input.modelName, input.parentId, input.ticker, input.interval, input.parentChartData);
  }

  public $indicatorSelect(input: any): void {
    const found = this._widget.widgets.find((widget) => widget.id === input.parentId);
    if (!found) return;

    found.inputs.activeIndicators = input.activeIndicators;
    this._widget.updateWidget(this.teamName, input.parentId, found);
  }

  public $tickSelect(input: any): void {
    this.addTickWidget(input.streamName, input.ticker, input.parentId, input.date);
  }

  /** A document already open in the same viewer is focused, not duplicated
   *  (the same file in a different viewer is a sibling tab — see PanelKind). */
  private _focusOpenDocument(name: WidgetService.WidgetName, key: string, value: string): boolean {
    if (!this.isDockviewMode || !value) return false;
    const hit = this._dockviewManifest.find(e => e.name === name && e.values?.inputs?.[key] === value);
    if (!hit || !this.wm.hasPanel(hit.id)) return false;
    this.wm.focusPanel(hit.id);
    return true;
  }

  // `host` on these inputs names the linked computer the path lives on (a channel
  // with affinity prints paths that are THERE); empty = the home. The commander
  // falls back to the home when the path is only there.
  public $editFile(input: any): void {
    console.log('edit file', input);
    if (this._focusOpenDocument('MonacoWidget', 'path', input.path)) return;
    this.addMonacoWidget(input.parentId, input.path, null, input.host || '');
  }

  public $previewFile(input: { parentId: string; path: string; previewType: 'image' | 'pdf'; host?: string }): void {
    console.log('preview file', input);
    if (this._focusOpenDocument('PreviewWidget', 'path', input.path)) return;
    this.addPreviewWidget(input.path, input.previewType, null, input.host || '');
  }

  public $viewMarkdown(input: { parentId: string; path: string; title: string; host?: string }): void {
    console.log('view markdown', input);
    const isUploadUrl = input.path.startsWith('/mux/') || input.path.startsWith('http');
    const src = isUploadUrl ? input.path
      : `${ConfigService.muxUrl}/commander/file?path=${encodeURIComponent(input.path)}${input.host ? `&host=${encodeURIComponent(input.host)}` : ''}`;
    if (this._focusOpenDocument('MarkdownWidget', 'src', src)) return;
    this.addMarkdownWidget({ src }, input.title);
  }

  public $viewCsv(input: { parentId: string; path: string; title: string; host?: string }): void {
    if (this._focusOpenDocument('DataViewerWidget', 'path', input.path)) return;
    this._addWidget('DataViewerWidget', null, {
      baseInputs: { path: input.path, fileType: 'csv', directPath: input.path, host: input.host || '' },
      defaultSpace: { cols: 55, rows: 35 }
    });
  }

  public $editWorkflow(input: any): void {
    this.addWorkflowWidget(input.name);
  }

  public $packetSelect(input: any): void {
    this.addPacketWidget(input.parentId, input.packetContent);
  }

  public $predictSelect(input: any): void {
    this.addModelInputWidget(input.parentId, input.timestamp, input.modelName, input.modelValues);
  }

  public $strategy(input: any): void {
    this.addStrategyWidget(input.parentId, input.modelName, input.ticker, input.strats);
  }

  public $strategyEditor(input: any): void {
    this.addStrategyEditorWidget(input.parentId, input.modelName, input.name);
  }

  public $loadBreakout(input: any): void {
    this.addBreakoutWidget(input.parentId, input.modelName, input.ticker, input.strategyName, input.days);
  }

  public $terminal(input: any): void {
    const command = input.command ? input.command : null;
    const title = input.title ? input.title : null;
    this.addTerminalWidget(command, title);
  }

  public $lab(input: any): void {
    this.addLabWidget(input.parentId);
  }

  public $inspect(input: any) {
    this.addInspectWidget(input.parentId, input.type, input.assetName, input.fileName);
  }

  public $experiments(input: any) {
    this.addExperimentsWidget(input.projectName);
  }

  public $definition(input: any) {
    this.addDefinitionsWidget(input.definitionName);
  }

  public $viewBuild(input: any) {
    this.addBuildsWidget(input);
  }

  public $viewAsset(input: any) {
    this.addAssetsWidget(input.assetName);
  }

  public $featureExplorer(input: any) {
    this.addFeatureSelectorWidget(input.projectName);
  }

  public $viewVault(input: any) {
    this.addVaultWidget();
  }

  public $viewVaultDetail(input: any) {
    this.addVaultDetailWidget(input.vaultName);
  }

  public $asset(input: any): void {
    this.addAssetsWidget();
  }

  public $openAsset(input: { parentId: string; assetName: string }): void {
    this.addAssetsWidget({ assetName: input.assetName });
  }

  public $upload() {
    this.addRawDataWidget();
  }


  public $compile(input: any) {
    this.addCompilerWidget(input.parentId, input.rdsName);
  }

  public $perfTableView(input: any) {
    this.addPerfTableWidget(input.run, input.displayedColumns, input.dynamicColumns, input.live);
  }

  public $viewDefinition(input: { path: string }) {
    this.addMonacoWidget(null, input.path);
  }

  public $diffDefinitions(input: { left: string; right: string }) {
    this.addModelDiffWidget(input.left, input.right);
  }

  public $iFrameFocus(input: any) {
    const item = this.dashboard.find((x) => x.id === input.id);
    if (!item) return;

    this.gridsterManager?.bringToFront(item.id);
    this._widget.updateWidget(this.teamName, item.id, item);
  }

  /** Open a team-scoped widget AT AN ADDRESS (docs/plans/org-policy.md, scope
   *  taxonomy 2026-09-01): the panel carries its own venue via inputs.teamName
   *  (wins over the grid team through the renderer), and wears it quietly in
   *  the title. Side-by-side across teams, like chats. */
  public $openWidgetForTeam(ev: { name: WidgetService.WidgetName; team: string; title?: string }): void {
    const base = ev.title || String(ev.name).replace(/Widget$/, '');
    // Through _addWidget like every working open path — it wires the titlebar
    // outputs and handles dockview vs grid; baseInputs.teamName is the venue
    // and wins over the grid team in the renderer.
    this._addWidget(ev.name, { title: `${base} · ${ev.team}` }, {
      baseInputs: { teamName: ev.team, title: `${base} · ${ev.team}` },
      defaultSpace: { x: 6, y: 2, rows: 30, cols: 44 } as any,
    });
  }

  public $openWidget(widgetName: WidgetService.WidgetName): void {
    switch (widgetName) {
      case 'AgentWidget':
        this.addAgentWidget(undefined, undefined, undefined, undefined, undefined);
        break;
      case 'ModelChartWidget':
        this.addModelWidget(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined
        );
        break;
      case 'ChatWidget':
        this.addChatWidget(undefined);
        break;
      case 'PromptsWidget':
        this.addPromptsWidget();
        break;
      case 'JobsWidget':
        this.addJobsWidget();
        break;
      case 'LLMWorkflowsWidget':
        this.addLLMWorkflowsWidget();
        break;
      case 'TemplateWidget':
        this.addTemplateWidget();
        break;
      case 'WorkflowWidget':
        this.addWorkflowWidget(undefined);
        break;
      case 'HostsWidget':
        this.addHostsWidget();
        break;
      case 'FilesWidget':
        this.addFilesWidget();
        break;
      case 'GPUsWidget':
        this.addGPUsWidget();
        break;
      case 'RawDataWidget':
        this.addRawDataWidget();
        break;
      case 'FeatureSelectorWidget':
        this.addFeatureSelectorWidget();
        break;
      case 'FeatureEditorWidget':
        this.addFeatureEditorWidget();
        break;
      case 'BuildsWidget':
        this.addBuildsWidget();
        break;
      case 'SchemasWidget':
        this.addSchemasWidget();
        break;
      case 'AssetsWidget':
        this.addAssetsWidget();
        break;
      case 'LabWidget':
        this.addLabWidget();
        break;
      case 'DefinitionsWidget':
        this.addDefinitionsWidget();
        break;
      case 'ExperimentsWidget':
        this.addExperimentsWidget();
        break;
      case 'VaultWidget':
        this.addVaultWidget();
        break;
      case 'PlaygroundWidget':
        this.addPlaygroundWidget();
        break;
      case 'StreamWidget':
        this.addStreamWidget(null);
        break;
      case 'IndicatorsWidget':
        this.addIndicatorsWidget();
        break;
      case 'SparklinesWidget':
        this.addSparklinesWidget();
        break;
      case 'StreamsWidget':
        this.addStreamsWidget();
        break;
      case 'DeploysWidget':
        this.addDeploysWidget();
        break;
      case 'LicenseWidget':
        this.addLicenseWidget();
        break;
      case 'TeamPolicyWidget':
        this.addTeamPolicyWidget();
        break;
      case 'TrustsWidget':
        this.addTrustsWidget();
        break;
      case 'SettingsWidget':
        this.openSettings();
        break;
      case 'TerminalWidget':
        this.addTerminalWidget(null, null);
        break;
      case 'TradingViewWidget':
        this.addTradingViewWidget();
        break;
      case 'HeartbeatWidget':
        this.addHeartbeatWidget();
        break;
      case 'KnowledgeGraphWidget':
        this.addKnowledgeGraphWidget();
        break;
      case 'BrowserWidget':
        this.addBrowserWidget();
        break;
      default:
        console.warn(`unknown widget: "${widgetName}"`);
        break;
    }
  }

  /**
   * Open a markdown file in the MarkdownWidget viewer (or blank if no path)
   */
  public $openMarkdownViewer(event: { path: string; title: string; startInEditMode?: boolean }): void {
    const inputs: any = { title: event.title };
    if (event.path) {
      inputs.src = `${ConfigService.muxUrl}/commander/file?path=${encodeURIComponent(event.path)}`;
      inputs.filePath = event.path;
    }
    if (event.startInEditMode) {
      inputs.startInEditMode = true;
    }
    this._addWidget('MarkdownWidget', undefined, {
      defaultSpace: { rows: 30, cols: 45 },
      baseInputs: inputs,
      extraOutputs: {
        onEditFile: this.$editFile.bind(this)
      }
    });
  }

  /**
   * Check if first-time setup is needed and open the Settings widget on Setup tab
   */
  private async checkFirstTimeSetup(): Promise<void> {
    try {
      // Once setup has been completed, never show the wizard again
      if (localStorage.getItem('lit-setup-completed')) return;

      // Check if Settings widget already exists (gridster or dockview)
      const hasSettings = this.isDockviewMode
        ? this._dockviewManifest.some(p => p.name === 'SettingsWidget')
        : this.dashboard.some(w => w.name === 'SettingsWidget');
      if (hasSettings) return;

      // In local mode, trust is not needed (no SSH). Otherwise check trust status.
      let isTrusted = ConfigService.localMode;
      if (!isTrusted) {
        const trustResponse: any = await lastValueFrom(
          this._http.get(`${ConfigService.apiUrl}/host-trust`)
        );
        isTrusted = trustResponse.isTrusted;
      }

      // Check if ANY backend is configured (Claude OR Gemini)
      let hasConfiguredBackend = false;
      try {
        const claudeStatus: any = await lastValueFrom(
          this._http.get(`${ConfigService.muxUrl}/backends/claude-cli/status`)
        );
        if (claudeStatus.auth_status === 'authenticated') {
          hasConfiguredBackend = true;
        }
      } catch (e) {
        console.debug('Claude status check failed:', e);
      }

      // A team seat counts: the backend status above reads only the user's flat
      // credential, while a policy-provisioned member signs into a team slot.
      if (!hasConfiguredBackend && this.teamName && this.teamName !== 'local') {
        try {
          const creds: any = await lastValueFrom(
            this._http.get(`${ConfigService.muxUrl}/credentials?team=${encodeURIComponent(this.teamName)}`)
          );
          hasConfiguredBackend = (creds?.credentials || []).some((c: any) => c.status === 'authed');
        } catch (e) {
          console.debug('Team credentials check failed:', e);
        }
      }

      if (!hasConfiguredBackend) {
        try {
          const geminiStatus: any = await lastValueFrom(
            this._http.get(`${ConfigService.muxUrl}/backends/gemini/status`)
          );
          if (geminiStatus.configured && geminiStatus.healthy) {
            hasConfiguredBackend = true;
          }
        } catch (e) {
          console.debug('Gemini status check failed:', e);
        }
      }

      // Check if any agents exist
      const agents = await this._agentService.loadAgents(this.teamName || undefined);
      const hasAgents = agents.length > 0;

      console.log('First-time setup check:', { localMode: ConfigService.localMode, isTrusted, hasConfiguredBackend, hasAgents, agentCount: agents.length });

      if (isTrusted && hasConfiguredBackend && hasAgents) {
        // All prerequisites met — mark setup as done permanently
        localStorage.setItem('lit-setup-completed', 'true');
        return;
      }

      // Prerequisites missing — show Settings on Setup tab ONCE per team in this
      // browser. Before this flag it reopened on every reload for as long as the
      // team stayed empty (Ben, app.lit.ai positronic, 2026-10-26: "just annoying").
      const shownKey = `lit-setup-shown-${this.teamName || 'local'}`;
      if (localStorage.getItem(shownKey)) return;
      setTimeout(() => {
        const alreadyOpen = this.isDockviewMode
          ? this._dockviewManifest.some(p => p.name === 'SettingsWidget')
          : this.dashboard.some(w => w.name === 'SettingsWidget');
        if (!alreadyOpen) {
          localStorage.setItem(shownKey, new Date().toISOString());
          this.addSettingsWidget(null, 'setup');
        }
      }, 500);
    } catch (error) {
      console.error('Error checking first-time setup status:', error);
    }
  }

  $click(_$event, item: WidgetService.Widget): void {
    if (!this.gridsterManager) return; // Only relevant for gridster mode
    this.dashboard.forEach((dashboardItem) => {
      if (dashboardItem.id == item.id) return;
      const containment = this.findContainment(item, dashboardItem);
      if (containment != 'outside' && dashboardItem.layerIndex > item.layerIndex) {
        const now: any = new Date();
        if (!dashboardItem.birthday || now - dashboardItem.birthday.getTime() > 1000) {
          item.layerIndex = this.gridsterManager.nextLayerIndex;
        }
      }
    });
  }

  /**
   * Check license status and show license widget if needed
   */
  private async checkLicenseStatus(): Promise<void> {
    try {
      const response: any = await lastValueFrom(this._http.get(`${ConfigService.apiUrl}/init/trial`));

      // Only add the license widget for trial or expired licenses
      if (!response.licensed && (response.active || !response.active)) {
        // Check if license widget already exists
        const existingWidget = this.dashboard.find(widget => widget.name === 'LicenseWidget');
        if (!existingWidget) {
          this.addLicenseWidget();
        }
      }
    } catch (error) {
      console.error('Error checking license status:', error);
    }
  }

  /**
   * Set up hourly license status check (called once from ngOnInit)
   */
  private setupLicenseCheckInterval(): void {
    this._licenseCheckSub = interval(3600000).subscribe(() => {
      this.checkLicenseStatus();
    });
  }

  /**
   * Open a dynamic widget (user-defined from /data/{team}/apps/)
   */
  public $openDynamicWidget(widget: DynamicWidget): void {
    if (widget.type === 'iframe' && widget.url) {
      this.addIframeWidget(widget.url, widget.title, widget.id);
    } else if (widget.type === 'component' && widget.element) {
      // Open web component app via ExternalAppWidget
      const app = {
        id: widget.id,
        manifest: {
          name: widget.name,
          title: widget.title,
          icon: widget.icon,
          element: widget.element,
          scripts: widget.scripts || [],
          styles: widget.styles || [],
          config: widget.config
        },
        source: widget.source,
        basePath: widget.basePath,
        loaded: false
      };
      // A panel-surface app is a place, not a document: one instance, focused on re-open.
      if (widget.surface === 'panel' && this.isDockviewMode) {
        const hit = this._dockviewManifest.find(e => e.name === 'ExternalAppWidget' && e.values?.inputs?.app?.id === app.id);
        if (hit && this.wm.hasPanel(hit.id)) { this.wm.focusPanel(hit.id); return; }
      }
      this.addExternalAppWidget(app, {}, null, widget.kind ? { kind: widget.kind } : null);
    } else {
      console.warn('Unsupported dynamic widget type:', widget.type);
    }
  }

  /**
   * Add an iframe widget for dynamic widgets
   */
  public addIframeWidget(url: string, title: string, id?: string): void {
    this._addWidget('IframeWidget', { id }, {
      defaultSpace: { rows: 35, cols: 55 },
      baseInputs: { url, title }
    });
  }

  /**
   * Add a markdown viewer widget
   * @param options Either { src: string } for URL or { content: string } for direct markdown
   * @param title Widget title
   * @param values Optional widget values for restore (position, size, id, etc.)
   */
  public addMarkdownWidget(options: { src?: string; content?: string }, title: string = 'Markdown', values = null): void {
    this._addWidget('MarkdownWidget', values, {
      defaultSpace: { rows: 30, cols: 45 },
      baseInputs: { ...options, title },
      extraOutputs: {
        onEditFile: this.$editFile.bind(this)
      }
    });
  }

  /**
   * Add an external web component app widget
   * @param app The external app object with manifest, basePath, etc.
   * @param config Optional configuration to pass to the app
   * @param basePath Optional base path override
   * @param values Optional widget values for restore (position, size, id, etc.)
   */
  public addExternalAppWidget(app: any, config: any = {}, basePath: string = null, values = null): void {
    this._addWidget('ExternalAppWidget', values, {
      defaultSpace: { rows: 25, cols: 40 },
      baseInputs: { app, config, basePath, title: app?.manifest?.title }
    });
  }

  /**
   * Open an ExternalAppWidget using raw inputs from a skill-pack AppItem.
   * Allows vendor env configs to declare external app widgets (e.g. appId: 'team:foo')
   * without requiring any lit-server code changes.
   */
  public $openExternalApp(inputs: Record<string, any>): void {
    this._addWidget('ExternalAppWidget', null, {
      defaultSpace: { minItemCols: 20, minItemRows: 15, cols: 50, rows: 30 },
      baseInputs: inputs,
    });
  }
}
